import React from 'react';
import { motion } from 'framer-motion';
import { Dumbbell as DumbbellIcon, School as Pool, Users, Heart, Coffee, Snowflake } from 'lucide-react';

const features = [
  {
    icon: DumbbellIcon,
    title: 'Premium Equipment',
    description: 'State-of-the-art fitness equipment for optimal training',
    image: 'https://i.ibb.co/LdtVZC4z/DSC02355.jpg',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: Pool,
    title: '25m Swimming Pool',
    description: 'Two-lane temperature-controlled pool for lap swimming and aqua classes',
    image: 'https://i.ibb.co/FknTZrrz/DSC01354.jpg',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    icon: Heart,
    title: 'Wellness Zone',
    description: 'Traditional sauna, steam room, ice bath, and spa facilities',
    image: 'https://i.ibb.co/kCQpt8n/Screenshot-2025-03-11-at-08-57-15.png',
    color: 'from-teal-500/20 to-emerald-500/20'
  },
  {
    icon: Users,
    title: 'Group Classes',
    description: 'Dynamic group fitness classes led by expert instructors',
    image: 'https://i.ibb.co/BVnknC24/DSC02235-1.jpg',
    color: 'from-emerald-500/20 to-blue-500/20'
  },
  {
    icon: Coffee,
    title: 'Café',
    description: 'Healthy meals and refreshments to fuel your workout',
    image: 'https://i.ibb.co/qMZKj2nf/DSC02495.jpg',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: Snowflake,
    title: 'Ice Bath',
    description: 'Cold immersion therapy at 4°C for recovery and performance',
    image: 'https://i.ibb.co/yngNWDxM/DSC01375.jpg',
    color: 'from-cyan-500/20 to-teal-500/20'
  }
];

export default function Features() {
  return (
    <section id="features" className="py-20 bg-white text-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">WORLD-CLASS FACILITIES</h2>
          <p className="text-xl text-gray-600">Experience fitness at its finest</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              className="group relative overflow-hidden rounded-2xl bg-black"
            >
              <div className="aspect-w-16 aspect-h-9 relative">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700 opacity-70"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors duration-500" />
                <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2, duration: 0.5 }}
                    className={`bg-gradient-to-br ${feature.color} p-4 rounded-full w-16 h-16 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500 mb-4`}
                  >
                    <div className="text-blue-400">
                      {React.createElement(feature.icon, { className: "h-8 w-8" })}
                    </div>
                  </motion.div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-200 transform opacity-0 group-hover:opacity-100 transition-all duration-500 text-sm sm:text-base">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}